import cn from "classnames"
import React from "react"
import { type FieldError, useFormContext, type RegisterOptions } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { type FormInputs } from "./form_input"
import Util from "@/common/util"
import { dig } from "@/common/util/misc"

export interface FormSelectProps
  extends React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> {
  groupId?: string
  label?: string | React.ReactNode
  rules?: RegisterOptions
  defaultValue?: string
  defaultLabel?: string
  values?: string[] | string[][]
  groupClassName?: string
}

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  groupId,
  rules,
  children,
  defaultValue,
  defaultLabel,
  values,
  groupClassName,
  ...props
}) => {
  if (!props.name) {
    throw new Error("Name is required")
  }

  const {
    register,
    formState: { errors },
  } = useFormContext<FormInputs>()
  const error = dig(errors, props.name) as FieldError | undefined
  const { t } = useTranslation()

  const options: string[][] =
    values && values.length > 0 && Array.isArray(values[0])
      ? (values as string[][])
      : (values as string[]).map(value => [value, value])

  label = props.required ? `* ${label}` : label

  const classname = cn("form-group relative", groupClassName, Util.snakeCase(props.name).replace(".", "_"), {
    "has-error has-feedback": error,
  })

  const registerFields = register(props.name, rules)

  return (
    <div className={classname} id={groupId}>
      {label && (
        <label htmlFor={props.id} className="paragraph-xs bold">
          {label}
        </label>
      )}
      <select
        className="form-control"
        {...registerFields}
        {...props}
        onChange={e => {
          registerFields.onChange(e)

          if (props.onChange) props.onChange(e)
        }}
      >
        {defaultLabel && <option value={defaultValue}>{defaultLabel}</option>}
        {options.map(option => (
          <option key={option[1]} value={option[1]}>
            {option[0]}
          </option>
        ))}
      </select>
      {error && <p className="field_error text-error">{error.message ? t(error.message) : t("misc.required_field")}</p>}
      {children}
    </div>
  )
}

export default FormSelect
